import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPrice } from "../lib/api";
import { ArrowRight, BadgeCheck, Headphones, MapPin, Sliders, ShoppingBag, Sparkles, WandSparkles } from "lucide-react";
import { VST_PROJECT_TEMPLATES } from "../data/vstTemplates";
import { useLocale } from "../context/LocaleContext";
import { localizeVstTemplate } from "../i18n/vstTemplateEnglish";

const HERO_IMG =
  "https://images.pexels.com/photos/10933686/pexels-photo-10933686.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function Home() {
  const { locale } = useLocale();
  const [engineers, setEngineers] = useState([]);
  const [engineerCount, setEngineerCount] = useState(0);
  const [gear, setGear] = useState([]);
  const [products, setProducts] = useState([]);
  const [templateCatalog, setTemplateCatalog] = useState({ templates: [], settings: [] });

  useEffect(() => {
    let active = true;

    const loadHome = async () => {
      const [engineersResult, gearResult, productsResult, templatesResult] = await Promise.allSettled([
        api.get("/engineers", { params: { page: 1 } }),
        api.get("/gear"),
        api.get("/products"),
        api.get("/vst-maker/templates/catalog"),
      ]);

      if (!active) return;

      if (engineersResult.status === "fulfilled") {
        setEngineers((engineersResult.value.data.items || []).slice(0, 4));
        setEngineerCount(engineersResult.value.data.total || 0);
      }
      if (gearResult.status === "fulfilled") {
        setGear(gearResult.value.data.slice(0, 6));
      }
      if (productsResult.status === "fulfilled") {
        setProducts(productsResult.value.data.slice(0, 3));
      }
      if (templatesResult.status === "fulfilled") {
        setTemplateCatalog(templatesResult.value.data || { templates: [], settings: [] });
      }
    };

    loadHome();
    return () => { active = false; };
  }, []);

  const featuredTemplates = useMemo(() => {
    const settings = Object.fromEntries((templateCatalog.settings || []).map((item) => [item.template_id, item]));
    const builtIns = new Map(VST_PROJECT_TEMPLATES.map((template) => [template.id, template]));
    const publishedIds = new Set((templateCatalog.templates || []).map((template) => template.id));
    const published = (templateCatalog.templates || []).map((template) => {
      const fallback = builtIns.get(template.id) || {};
      const setting = settings[template.id] || {};
      return {
        ...fallback,
        id: template.id,
        name: template.name || fallback.name,
        description: template.description || fallback.description,
        pluginKind: template.plugin_kind || fallback.pluginKind || "instrument",
        instrumentType: template.instrument_type || fallback.instrumentType || "Instrument",
        accentColor: template.accent_color || fallback.accentColor || "#e11d48",
        highlights: template.highlights?.length ? template.highlights : fallback.highlights || [],
        thumbnailUrl: setting.thumbnail_url || template.thumbnail_url || template.background_url || fallback.backgroundUrl || "",
      };
    });
    const remaining = VST_PROJECT_TEMPLATES
      .filter((template) => !publishedIds.has(template.id))
      .map((template) => ({ ...template, thumbnailUrl: settings[template.id]?.thumbnail_url || template.backgroundUrl || "" }));
    const visible = [...published, ...remaining]
      .filter((template) => !settings[template.id]?.hidden)
      .map((template, index) => ({ template, index, order: Number.isInteger(settings[template.id]?.sort_order) ? settings[template.id].sort_order : 10000 + index }))
      .sort((left, right) => left.order - right.order || left.index - right.index)
      .map(({ template }) => template);
    return [...visible.filter((template) => template.thumbnailUrl), ...visible.filter((template) => !template.thumbnailUrl)]
      .slice(0, 3)
      .map((template) => localizeVstTemplate(template, locale));
  }, [locale, templateCatalog]);

  return (
    <div data-testid="home-page" className="pb-32">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden grain-overlay">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/60 to-black/30" />
        <div className="absolute top-32 right-12 hidden lg:flex flex-col gap-1.5 opacity-70">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="eq-bar w-2 h-12 bg-[#e11d48] rounded-sm" />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-32 w-full fade-up">
          <div className="flex items-center gap-2 mb-6 text-[#e11d48]">
            <span className="w-2 h-2 rounded-full bg-[#e11d48] animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em]">Recording · Mixing · Mastering</span>
          </div>
          <h1 className="font-[Outfit] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.95] max-w-4xl">
            Sound that <span className="italic font-light">moves</span>
            <br />
            from <span className="text-[#e11d48]">three sides.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-300 max-w-xl leading-relaxed">
            A production studio crafting music records, curating world-class gear,
            and delivering digital audio tools for forward-thinking creators.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/vst-maker"
              data-testid="hero-cta-vst-maker"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] font-semibold text-white transition-all glow-brand"
            >
              <WandSparkles className="w-4 h-4" />
              Create Your VST
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/shop"
              data-testid="hero-cta-shop"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 hover:bg-white/5 font-semibold text-white transition-all"
            >
              Explore the Shop
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/engineers"
              data-testid="hero-cta-engineers"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 hover:bg-white/5 font-semibold text-white transition-all"
            >
              <Headphones className="w-4 h-4" />
              Find an Engineer
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { to: "/engineers", title: "Engineer Marketplace", desc: "Find the right engineer for your next release.", Icon: Headphones, count: engineerCount, unit: "engineers" },
            { to: "/gear", title: "Studio Gear", desc: "The tools behind the sound.", Icon: Sliders, count: gear.length },
            { to: "/shop", title: "Digital Shop", desc: "Sample packs, presets, and templates.", Icon: ShoppingBag, count: products.length },
          ].map((c, i) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`category-${c.title.toLowerCase().replace(/\s/g, "-")}`}
              className="group relative overflow-hidden bg-[#0a0a0c] border border-white/10 rounded-2xl p-8 hover:border-[#e11d48]/50 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#e11d48]/10 blur-3xl group-hover:bg-[#e11d48]/20 transition-colors" />
              <c.Icon className="w-8 h-8 text-[#e11d48] mb-12" />
              <div className="text-xs font-mono text-zinc-500 mb-1">0{i + 1} / 03</div>
              <h3 className="font-[Outfit] text-2xl font-bold mb-2">{c.title}</h3>
              <p className="text-sm text-zinc-400 mb-6">{c.desc}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-500">{c.count} {c.unit || "items"}</span>
                <ArrowRight className="w-4 h-4 text-[#e11d48] group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* VST MAKER TEMPLATES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="flex items-end justify-between gap-5 mb-10">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#e11d48] mb-3"><WandSparkles className="h-4 w-4" /> VST Maker</div>
            <h2 className="font-[Outfit] text-4xl md:text-5xl font-bold tracking-tight">Start Building Your Plugin</h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-400">Choose a ready-made instrument or audio effect, customize the interface and DSP, then build your own native VST3.</p>
          </div>
          <Link to="/vst-maker#design-templates" className="hidden shrink-0 items-center gap-2 text-sm text-zinc-400 hover:text-white sm:flex">
            View All <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {featuredTemplates.map((template) => (
            <Link
              key={template.id}
              to={`/vst-studio?template=${encodeURIComponent(template.id)}`}
              className="group overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0c] transition-all duration-300 hover:-translate-y-1 hover:border-[#e11d48]/45"
            >
              <div className="relative aspect-[16/9] overflow-hidden bg-[radial-gradient(circle_at_30%_20%,rgba(225,29,72,.2),transparent_38%),#111116]">
                {template.thumbnailUrl && <img src={template.thumbnailUrl} alt={`Template ${template.name}`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
                <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/55 px-3 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-white backdrop-blur" style={{ color: template.accentColor }}>{template.pluginKind === "effect" ? "Plugin FX" : "VST Instrument"}</span>
                <div className="absolute inset-x-0 bottom-0 p-5"><div className="text-[9px] font-bold uppercase tracking-[0.16em] text-zinc-400">{template.instrumentType}</div><h3 className="mt-1 font-[Outfit] text-xl font-bold text-white">{template.name}</h3></div>
              </div>
              <div className="p-5">
                <p className="line-clamp-2 min-h-10 text-xs leading-5 text-zinc-500">{template.description}</p>
                <div className="mt-4 flex items-center justify-between gap-3"><div className="flex min-w-0 gap-1.5 overflow-hidden">{(template.highlights || []).slice(0, 2).map((highlight) => <span key={highlight} className="whitespace-nowrap rounded-full border border-white/[0.07] px-2 py-1 text-[8px] text-zinc-400">{highlight}</span>)}</div><ArrowRight className="h-4 w-4 shrink-0 text-[#e11d48] transition-transform group-hover:translate-x-1" /></div>
              </div>
            </Link>
          ))}
        </div>
        <Link to="/vst-maker#design-templates" className="mt-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white sm:hidden">View All Templates <ArrowRight className="h-3.5 w-3.5" /></Link>
      </section>

      {/* FEATURED ENGINEERS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#e11d48] mb-3">Engineer Marketplace</div>
            <h2 className="font-[Outfit] text-4xl md:text-5xl font-bold tracking-tight">Featured Engineers</h2>
          </div>
          <Link to="/engineers" className="text-sm text-zinc-400 hover:text-white flex items-center gap-2">
            View All <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {engineers.map((engineer) => (
            <Link
              to="/engineers"
              key={engineer.id}
              data-testid={`featured-engineer-${engineer.id}`}
              className="group overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0c] transition-all duration-300 hover:-translate-y-1 hover:border-[#e11d48]/40"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-zinc-950">
                {engineer.hero_image_url || engineer.avatar_url ? (
                  <img src={engineer.hero_image_url || engineer.avatar_url} alt={`${engineer.name} profile`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#e11d48]/30 to-violet-600/20 font-[Outfit] text-3xl font-black text-white">
                    {engineer.name?.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase()}
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                {engineer.marketplace_tier === "verified" && <span title="Verified Engineer" className="absolute right-3 top-3 inline-flex rounded-full bg-sky-500 p-1 text-white shadow-lg"><BadgeCheck className="h-4 w-4" /></span>}
                <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                  <h3 className="truncate font-[Outfit] text-base font-bold sm:text-lg">{engineer.name}</h3>
                  <p className="mt-0.5 line-clamp-1 text-[10px] text-zinc-300 sm:text-xs">{engineer.professional_title}</p>
                  {engineer.location && <div className="mt-2 flex items-center gap-1 text-[9px] text-zinc-400 sm:text-[10px]"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{engineer.location}</span></div>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* GEAR PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#e11d48] mb-3">TripleSide Studio</div>
            <h2 className="font-[Outfit] text-4xl md:text-5xl font-bold tracking-tight">Tools of the Trade</h2>
          </div>
          <Link to="/gear" className="text-sm text-zinc-400 hover:text-white flex items-center gap-2">
            View All <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {gear.slice(0, 6).map((g) => (
            <div
              key={g.id}
              data-testid={`featured-gear-${g.id}`}
              className="group relative aspect-[4/5] overflow-hidden rounded-xl border border-white/10 hover:border-white/20 transition-all"
            >
              <img src={g.image_url} alt={g.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="text-[10px] font-mono text-[#e11d48] uppercase tracking-wider mb-1">{g.category}</div>
                <div className="font-bold text-lg leading-tight">{g.name}</div>
                <div className="text-xs text-zinc-300 mt-0.5">{g.brand}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DIGITAL PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#e11d48] mb-3">Digital Goods</div>
            <h2 className="font-[Outfit] text-4xl md:text-5xl font-bold tracking-tight">Shop the Sound</h2>
          </div>
          <Link to="/shop" className="text-sm text-zinc-400 hover:text-white flex items-center gap-2">
            View All <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {products.map((p) => (
            <Link
              to={`/shop/${p.id}`}
              key={p.id}
              data-testid={`featured-product-${p.id}`}
              className="group bg-[#0a0a0c] border border-white/10 rounded-2xl overflow-hidden hover:border-[#e11d48]/40 transition-all"
            >
              <div className="aspect-square overflow-hidden">
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-5 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono text-[#e11d48] uppercase tracking-wider mb-1">{p.category}</div>
                  <div className="font-semibold truncate">{p.name}</div>
                </div>
                <div className="font-[Outfit] text-xl font-bold flex-shrink-0 ml-3">{fmtPrice(p.price)}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0c] p-12 md:p-20">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#e11d48]/20 blur-3xl" />
          <Sparkles className="w-8 h-8 text-[#e11d48] mb-6 relative" />
          <h3 className="font-[Outfit] text-3xl md:text-5xl font-bold tracking-tight max-w-2xl relative">
            Ready to make your next record?
          </h3>
          <p className="mt-4 text-zinc-400 max-w-xl relative">
            Book the studio, license a sample pack, or just nerd out about gear. We&apos;re here for it.
          </p>
          <div className="mt-8 flex gap-3 relative">
            <Link to="/shop" className="px-7 py-3.5 rounded-full bg-[#e11d48] font-semibold hover:bg-[#be123c] transition-colors">
              Browse Shop
            </Link>
            <a href="https://api.whatsapp.com/send/?phone=6287708772747&text&type=phone_number&app_absent=0" className="px-7 py-3.5 rounded-full border border-white/20 font-semibold hover:bg-white/5 transition-colors">
              Contact Studio
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
